/**
 * PasswordExpiredPage.jsx
 * 관리자 비밀번호 만료 페이지
 * 
 * 비밀번호 만료 시 강제로 비밀번호 변경을 요구하는 페이지
 * 비밀번호 만료 임박 시 경고와 함께 변경 옵션 제공
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import adminAccountService from '../../services/adminAccountService';
import passwordPolicyService from '../../services/passwordPolicyService';
import { openAlert, openConfirm } from '../../utils/common';
import '../../assets/css/login.css';
import '../../assets/css/password-expired.css';

const PasswordExpiredPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated } = useAuth();
    
    // 비밀번호 입력 상태
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    
    // UI 상태
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false
    });
    
    // 비밀번호 강도 상태
    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        errors: []
    });
    
    // 비밀번호 정책 상태
    const [passwordPolicy, setPasswordPolicy] = useState({
        adminExpiryDays: 90, // 기본값
        userExpiryDays: 180
    });
    
    // 만료 정보 (location.state에서 전달받음)
    const isInitialPassword = location.state?.isInitialPassword ?? false;
    const customMessage = location.state?.message ?? '';
    const daysUntilExpiry = location.state?.daysUntilExpiry ?? 0;
    const isExpired = location.state?.isExpired ?? daysUntilExpiry <= 0;
    const canSkip = location.state?.canSkip ?? false;
    const adminId = location.state?.adminId ?? '';
    
    // 비밀번호 정책 조회
    useEffect(() => {
        const fetchPasswordPolicy = async () => {
            try {
                const policy = await passwordPolicyService.getPolicies();
                setPasswordPolicy({
                    adminExpiryDays: policy.adminExpiryDays || 90,
                    userExpiryDays: policy.userExpiryDays || 180
                });
            } catch (error) {
                console.error('비밀번호 정책 조회 실패:', error);
                // 실패 시 기본값 사용
            }
        };
        fetchPasswordPolicy();
    }, []);
    
    // 로그인되지 않은 경우 로그인 페이지로 리다이렉트
    useEffect(() => {
        if (!isAuthenticated && !adminId) {
            navigate('/login');
        }
    }, [isAuthenticated, adminId, navigate]);
    
    /**
     * 비밀번호 강도 계산
     */
    const calculatePasswordStrength = (password) => {
        if (!password) {
            return { score: 0, errors: [] };
        }
        
        let score = 0;
        const errors = [];
        
        // 길이 점수 (최대 40점)
        if (password.length >= 8) score += 20;
        if (password.length >= 12) score += 10;
        if (password.length >= 16) score += 10;
        else if (password.length < 8) errors.push('8자 이상 입력해주세요');
        
        // 복잡도 점수 (최대 60점)
        const hasLowerCase = /[a-z]/.test(password);
        const hasUpperCase = /[A-Z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
        
        if (hasLowerCase) score += 15;
        else errors.push('소문자를 포함해주세요');
        
        if (hasUpperCase) score += 15;
        else errors.push('대문자를 포함해주세요');
        
        if (hasNumbers) score += 15;
        else errors.push('숫자를 포함해주세요');
        
        if (hasSpecialChars) score += 15;
        else errors.push('특수문자를 포함해주세요');
        
        return { score, errors };
    };
    
    /**
     * 새 비밀번호 변경 핸들러
     */
    const handleNewPasswordChange = (e) => {
        const value = e.target.value;
        setNewPassword(value);
        
        // 비밀번호 강도 계산
        const strength = calculatePasswordStrength(value);
        setPasswordStrength(strength);
    };
    
    /**
     * 비밀번호 강도 레벨 반환
     */
    const getStrengthLevel = (score) => {
        if (score >= 80) return { text: '강함', color: '#4caf50', width: '100%' };
        if (score >= 60) return { text: '보통', color: '#ff9800', width: '66%' };
        if (score >= 40) return { text: '약함', color: '#f44336', width: '33%' };
        return { text: '매우 약함', color: '#9e9e9e', width: '10%' };
    };
    
    /**
     * 만료 메시지 반환
     */
    const getExpiryMessage = () => {
        // 초기 비밀번호인 경우
        if (isInitialPassword) {
            return '초기 비밀번호 변경 필요';
        }
        // 비밀번호 만료
        if (daysUntilExpiry <= 0) {
            return '비밀번호가 만료되었습니다';
        } else if (daysUntilExpiry === 1) {
            return '비밀번호가 내일 만료됩니다';
        } else {
            return `비밀번호가 ${daysUntilExpiry}일 후 만료됩니다`;
        }
    };
    
    /**
     * 비밀번호 변경 성공 후 대시보드로 이동
     */
    const handlePasswordChangeSuccess = () => {
        navigate('/');
    };
    
    // window에 콜백 함수 등록
    useEffect(() => {
        window.handlePasswordChangeSuccess = handlePasswordChangeSuccess;
        return () => { delete window.handlePasswordChangeSuccess; };
    }, []);
    
    /**
     * 비밀번호 변경 처리
     */
    const handlePasswordChange = async (e) => {
        e.preventDefault();
        
        // 1. 입력 검증
        if (!currentPassword) {
            openAlert({ title: '입력 오류', description: '현재 비밀번호를 입력해주세요' });
            return;
        }
        
        if (!newPassword) {
            openAlert({ title: '입력 오류', description: '새 비밀번호를 입력해주세요' });
            return;
        }
        
        if (!confirmPassword) {
            openAlert({ title: '입력 오류', description: '비밀번호 확인을 입력해주세요' });
            return;
        }
        
        // 2. 비밀번호 일치 확인
        if (newPassword !== confirmPassword) {
            openAlert({ title: '입력 오류', description: '새 비밀번호가 일치하지 않습니다' });
            return;
        }
        
        // 3. 현재 비밀번호와 새 비밀번호 동일 확인
        if (currentPassword === newPassword) {
            openAlert({ title: '입력 오류', description: '현재 비밀번호와 다른 비밀번호를 입력해주세요' });
            return;
        }
        
        // 4. 비밀번호 강도 확인
        if (passwordStrength.score < 60) {
            const confirmResult = window.confirm(
                '비밀번호가 약합니다. 계속 진행하시겠습니까?\n\n' +
                passwordStrength.errors.join('\n')
            );
            if (!confirmResult) {
                return;
            }
        }
        
        setIsLoading(true);
        
        try {
            // 5. API 호출
            const result = await adminAccountService.changeMyPassword(currentPassword, newPassword);
            
            if (result.success) {
                openConfirm({
                    title: '성공',
                    description: '비밀번호가 성공적으로 변경되었습니다.',
                    positive_method: 'handlePasswordChangeSuccess',
                    positive_btn: '확인',
                    negative_method: null
                });
            } else {
                throw new Error(result.message || '비밀번호 변경에 실패했습니다');
            }
        } catch (error) {
            console.error('Password change error:', error);
            openAlert({ 
                title: '오류', 
                description: error.message || '비밀번호 변경 중 오류가 발생했습니다'
            });
        } finally {
            setIsLoading(false);
        }
    };
    
    /**
     * 취소 핸들러 (만료되지 않은 경우에만 표시)
     */
    const handleCancel = () => {
        if (isExpired) {
            openAlert({ 
                title: '알림', 
                description: '비밀번호가 만료되어 변경이 필수입니다' 
            });
            return;
        }
        
        navigate('/settings');
    };
    
    const strengthLevel = getStrengthLevel(passwordStrength.score);
    
    return (
      <div className="login_wrap">
        <div className="login_box">
          {/* 로고 */}
          <div className="login_logo">
            <img src="/adm/images/login_logo.png" alt="GlucoSOUND" />
          </div>

          {/* 제목 */}
          <div className="login_title">비밀번호 변경</div>

          {/* 만료 경고 */}
          <div
            className={`password_expired_alert ${
              isInitialPassword ? "initial" : isExpired ? "expired" : "warning"
            }`}
          >
            <div className="icon">
              {isInitialPassword ? "🔑" : isExpired ? "⚠️" : "⏰"}
            </div>
            <div className="message">
              <div className="title">{getExpiryMessage()}</div>
              <div className="description">
                {customMessage || "보안을 위해 비밀번호를 변경해주세요"}
              </div>
            </div>
          </div>

          <form onSubmit={handlePasswordChange} className="login_input">
            {/* SI-05: 현재 비밀번호 (8~50자) */}
            <div className="password_input_wrapper">
              <input
                type={showPassword.current ? "text" : "password"}
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="현재 비밀번호"
                maxLength={50}
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() =>
                  setShowPassword({
                    ...showPassword,
                    current: !showPassword.current,
                  })
                }
                tabIndex={-1}
                className="password_toggle_btn"
              >
                {showPassword.current ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>

            {/* SI-05: 새 비밀번호 (8~50자) */}
            <div className="password_input_wrapper">
              <input
                type={showPassword.new ? "text" : "password"}
                id="newPassword"
                value={newPassword}
                onChange={handleNewPasswordChange}
                placeholder="새 비밀번호 (8자 이상)"
                maxLength={50}
                disabled={isLoading}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() =>
                  setShowPassword({ ...showPassword, new: !showPassword.new })
                }
                tabIndex={-1}
                className="password_toggle_btn"
              >
                {showPassword.new ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>

            {/* 비밀번호 강도 표시 */}
            {newPassword && (
              <div className="password_strength_container">
                <div className="password_strength_bar_container">
                  <div
                    className="password_strength_bar"
                    style={{
                      width: strengthLevel.width,
                      backgroundColor: strengthLevel.color,
                    }}
                  />
                </div>
                <div
                  className="password_strength_text"
                  style={{ color: strengthLevel.color }}
                >
                  {strengthLevel.text} ({passwordStrength.score}점)
                </div>
                {passwordStrength.errors.length > 0 && (
                  <div className="password_strength_errors">
                    {passwordStrength.errors.map((error, index) => (
                      <div key={index}>• {error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* SI-05: 비밀번호 확인 (8~50자) */}
            <div className="password_input_wrapper">
              <input
                type={showPassword.confirm ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="비밀번호 확인"
                maxLength={50}
                disabled={isLoading}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() =>
                  setShowPassword({
                    ...showPassword,
                    confirm: !showPassword.confirm,
                  })
                }
                tabIndex={-1}
                className="password_toggle_btn"
              >
                {showPassword.confirm ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
            {confirmPassword && newPassword !== confirmPassword && (
              <div className="password_error_message">
                비밀번호가 일치하지 않습니다
              </div>
            )}

            {/* 버튼 */}
            <button
              type="submit"
              className="login_btn password_submit_btn"
              disabled={
                isLoading ||
                !currentPassword ||
                !newPassword ||
                !confirmPassword ||
                newPassword !== confirmPassword
              }
            >
              {isLoading ? "변경 중..." : "비밀번호 변경"}
            </button>

            {canSkip && !isExpired && !isInitialPassword && (
              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className="password_cancel_btn"
              >
                나중에 변경
              </button>
            )}
          </form>

          {/* 비밀번호 정책 안내 */}
          <div className="password_policy_box">
            <div className="title">비밀번호 정책</div>
            <div className="content">
              • 최소 8자 이상
              <br />
              • 대문자, 소문자, 숫자, 특수문자 포함 권장
              <br />
              • 이전 비밀번호와 다른 비밀번호 사용
              <br />• 비밀번호는 {passwordPolicy.adminExpiryDays}일마다 변경
              필요 (정책 설정에서 변경 가능)
            </div>
          </div>
        </div>
      </div>
    );
};

export default PasswordExpiredPage;
